import React, { useState } from 'react';
import {
  ArrowRight, Cpu, Zap, Shield,
  Target, Eye,
  Brain, Camera, Package, DollarSign, Monitor,
  Bot, Workflow, Wrench, PanelTop, Binary,
  Mail, MessageCircle, MapPin, Send, X, Database
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { useLanguage } from '../context/LanguageContext';

const Home = () => {
  const { t } = useLanguage();
  const [lightboxImage, setLightboxImage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const top = element.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const recaptchaValue = window.grecaptcha ? window.grecaptcha.getResponse() : '';
    if (!recaptchaValue) {
      toast.error('Please complete the reCAPTCHA');
      return;
    }
    setIsSubmitting(true);
    try {
      const EMAILJS_SERVICE_ID = 'service_vo5pbma';
      const EMAILJS_TEMPLATE_ID = 'template_gn89512';
      const EMAILJS_PUBLIC_KEY = 'UfVAXDLWBbPSWotIe';

      const emailJsData = {
        service_id: EMAILJS_SERVICE_ID,
        template_id: EMAILJS_TEMPLATE_ID,
        user_id: EMAILJS_PUBLIC_KEY,
        template_params: {
          from_name: formData.name,
          reply_to: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message,
          to_name: 'Arrigo IOT Sales',
          'g-recaptcha-response': recaptchaValue
        }
      };

      const response = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailJsData),
      });

      if (!response.ok) {
        throw new Error('Failed to send message via EmailJS');
      }

      toast.success(t.contact.successMessage);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      if (window.grecaptcha) {
        window.grecaptcha.reset();
      }
    } catch (err) {
      console.error('Contact form error:', err);
      toast.error(err.message || 'Could not send message. Please try again or email sales@arrigo-aiot.com directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    { icon: Brain, title: t.products.benefit1Title, description: t.products.benefit1Desc },
    { icon: Camera, title: t.products.benefit2Title, description: t.products.benefit2Desc },
    { icon: Package, title: t.products.benefit3Title, description: t.products.benefit3Desc },
    { icon: DollarSign, title: t.products.benefit4Title, description: t.products.benefit4Desc }
  ];

  const productFeatures = [
    { icon: Cpu, label: 'Edge Computing' },
    { icon: Zap, label: 'Real-time Processing' },
    { icon: Shield, label: 'Industrial Grade' },
    { icon: Monitor, label: 'Live Dashboard' }
  ];

  const applicationExamples = [
    {
      title: 'Object Counting Detection',
      image: '/images/1nm44bnl_Object_Counting_Detection.jpg'
    },
    {
      title: 'Oriented Object Detection',
      image: '/images/lbggoanw_Object_Oriented_Detection.jpg'
    },
    {
      title: 'OCR Detection',
      image: '/images/ppmryy1a_OCR_Detection.jpg'
    },
    {
      title: 'Defect Detection',
      image: '/images/wmtsxn83_Defect_Detection.jpg'
    },
    {
      title: 'PokaYoke Screw Assembly',
      image: '/images/7ujf7qh8_PokaYoke_Screw_Assembly.jpg'
    },
    {
      title: 'PPE Compliance Human Detection',
      image: '/images/hh1ckqdu_PPE_Compliance_Human_Detection.png'
    },
    {
      title: 'SOP Compliance',
      image: '/images/d4bqi8wj_SOP_Compliance.jpg'
    },
    {
      title: 'Object Detection',
      image: '/images/y8yg0hq7_Object_Detection.jpg'
    },
    {
      title: 'Object Counting Detection 2',
      image: '/images/2j9l4yzt_Object_Counting_Detection2.jpg'
    }
  ];

  const services = [
    {
      icon: Bot,
      title: t.services.service1,
      description: t.services.service1Desc,
      image: '/images/2t50p3w1_Z1.jpg'
    },
    {
      icon: Cpu,
      title: t.services.service6,
      description: t.services.service6Desc,
      image: '/images/aipgoy8u_Z2.png'
    },
    {
      icon: Database,
      title: t.services.service7,
      description: t.services.service7Desc,
      image: '/images/Q9.png'
    },
    {
      icon: Workflow,
      title: t.services.service2,
      description: t.services.service2Desc,
      image: '/images/ja2n1sva_Z3.png'
    },
    {
      icon: Binary,
      title: t.services.service5,
      description: t.services.service5Desc,
      image: '/images/z66p2a2p_Z4.jpg'
    },
    {
      icon: PanelTop,
      title: t.services.service4,
      description: t.services.service4Desc,
      image: '/images/bmyuh1o0_Z5.png'
    },
    {
      icon: Wrench,
      title: t.services.service3,
      description: t.services.service3Desc,
      image: '/images/nukdf1gc_Z6.jpg'
    }
  ];

  const contactInfo = [
    { icon: Mail, title: 'Email', content: 'sales@arrigo-aiot.com', link: 'mailto:sales@arrigo-aiot.com' },
    { icon: MessageCircle, title: 'WhatsApp', content: 'Chat with us on WhatsApp', link: 'https://wa.me/6285226698558?text=Hi%20Arrigo%20IOT%2C%20I%20want%20to%20ask%20about%20/%20Saya%20ingin%20bertanya%20tentang...' },
    { icon: MapPin, title: t.contact.address, content: t.contact.addressText, link: null }
  ];

  return (
    <div className="min-h-screen">
      {/* HERO SECTION */}
      <section id="home" className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/dhrp52sw_A1.jpeg"
            alt="AI Vision Technology"
            className="w-full h-full object-cover"
            style={{ filter: 'contrast(1.1) saturate(1.15) brightness(1.05)' }}
            data-testid="hero-image"
          />
          {/* Lighter gradient overlay for better image visibility */}
          <div className="absolute inset-0 bg-gradient-to-r from-purple-900/70 via-purple-900/30 to-blue-900/60" />
          <div className="absolute inset-0 bg-gradient-to-b from-purple-900/40 via-transparent to-gray-900/50" />
        </div>

        <div className="container mx-auto px-4 z-10 text-center text-white">
          <div className="max-w-4xl mx-auto space-y-8 animate-fade-in">
            <h1 className="text-5xl md:text-7xl font-bold leading-tight drop-shadow-2xl" data-testid="hero-tagline" style={{ textShadow: '0 4px 20px rgba(0,0,0,0.5)' }}>
              {t.hero.tagline}
            </h1>
            <p className="text-xl md:text-2xl text-purple-50 font-light drop-shadow-lg" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.6)' }}>
              {t.hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
              <Button
                size="lg"
                onClick={() => scrollToSection('products')}
                className="bg-white dark:bg-slate-900 text-purple-700 dark:text-purple-400 hover:bg-purple-50 dark:bg-slate-800 px-8 py-6 text-lg font-semibold transition-all hover:scale-105 shadow-xl"
                data-testid="hero-cta-button"
              >
                {t.hero.cta}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => scrollToSection('about')}
                className="border-2 border-white text-white hover:bg-white dark:bg-slate-900 hover:text-purple-700 dark:text-purple-400 px-8 py-6 text-lg font-semibold transition-all hover:scale-105 bg-transparent"
                data-testid="hero-learn-more-button"
              >
                {t.hero.learnMore}
              </Button>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <div className="w-6 h-10 border-2 border-white rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white dark:bg-slate-900 rounded-full animate-bounce" />
          </div>
        </div>
      </section>

      {/* ABOUT SECTION */}
      <section id="about" className="py-20 bg-white dark:bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <span className="inline-block px-4 py-2 bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-400 rounded-full text-sm font-semibold mb-8">
              {t.about.title}
            </span>

            {/* Logo + Motto */}
            <div className="flex flex-col items-center mb-10">
              <img
                src="/images/lxb2qtht_C5.png"
                alt="Arrigo IOT Logo"
                className="w-48 h-48 md:w-56 md:h-56 lg:w-64 lg:h-64 object-contain drop-shadow-2xl"
                data-testid="about-logo"
              />
              <p className="mt-6 text-3xl md:text-4xl font-semibold bg-gradient-to-r from-purple-700 to-blue-600 bg-clip-text text-transparent italic" data-testid="about-motto">
                "Achieving Sustainable Goodness"
              </p>
            </div>

            <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed text-justify">
              <strong>PT. Arrigo Inti Otomasi Teknologi</strong>
              {t.about.description.replace('PT. Arrigo Inti Otomasi Teknologi', '')}
            </p>
          </div>

          {/* Mission & Vision */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
            <div className="bg-gradient-to-br from-blue-50 dark:from-slate-800 to-purple-50 dark:to-slate-900 p-10 rounded-2xl shadow-lg hover:shadow-2xl transition-shadow">
              <div className="w-16 h-16 rounded-xl bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center mb-6">
                <Eye className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t.about.vision}</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-justify">{t.about.visionText}</p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 dark:from-slate-900 to-blue-50 dark:to-slate-800 p-10 rounded-2xl shadow-lg hover:shadow-2xl transition-shadow">
              <div className="w-16 h-16 rounded-xl bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t.about.mission}</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-justify">{t.about.missionText}</p>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCTS SECTION */}
      <section id="products" className="py-20 bg-gray-50 dark:bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-400 rounded-full text-sm font-semibold mb-4">
              {t.products.title}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4" data-testid="products-title">
              {t.products.mainProduct}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400">{t.products.subtitle}</p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <div
                  className="relative rounded-2xl overflow-hidden shadow-2xl group cursor-zoom-in"
                  onClick={() => setLightboxImage({ src: '/images/ftdcp5ee_A3.png', title: '' })}
                >
                  <img
                    src="/images/ftdcp5ee_A3.png"
                    alt="Arrigo AIVC-3000I"
                    className="w-full h-auto transition-transform group-hover:scale-105"
                    data-testid="product-image"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3 mt-6">
                  {productFeatures.map((feature, index) => {
                    const Icon = feature.icon;
                    return (
                      <div key={index} className="bg-white dark:bg-slate-900 p-3 rounded-xl flex items-center space-x-3 shadow-sm">
                        <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                        </div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{feature.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-6">
                <h3 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white" data-testid="product-name">
                  {t.products.productName}
                </h3>
                <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed text-justify">
                  {t.products.productDesc}
                </p>
                <Button
                  size="lg"
                  onClick={() => scrollToSection('contact')}
                  className="bg-gradient-to-r from-purple-700 to-blue-600 hover:from-purple-800 hover:to-blue-700 text-white px-8 py-6 text-lg font-semibold transition-all hover:scale-105 shadow-lg"
                  data-testid="inquire-button"
                >
                  {t.products.inquire}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Benefits */}
            <h3 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-10">
              {t.products.benefitsTitle}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <Card
                    key={index}
                    className="border-0 shadow-lg hover:shadow-2xl transition-all hover:-translate-y-1"
                    data-testid={`benefit-card-${index}`}
                  >
                    <CardHeader>
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-100 to-blue-100 dark:from-slate-800 dark:to-slate-700 flex items-center justify-center mb-4">
                        <Icon className="w-7 h-7 text-purple-600 dark:text-purple-400" />
                      </div>
                      <CardTitle className="text-xl">{benefit.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base text-gray-600 dark:text-gray-400 text-justify">
                        {benefit.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Product Flyer */}
            <div className="mt-16 flex justify-center">
              <div
                className="relative rounded-2xl overflow-hidden shadow-2xl cursor-zoom-in max-w-4xl w-full group"
                onClick={() => setLightboxImage({ src: '/images/tksk7rtl_Flyer.jpeg', title: 'AIVC-3000I Product Flyer' })}
                data-testid="product-flyer"
              >
                <img
                  src="/images/tksk7rtl_Flyer.jpeg"
                  alt="AIVC-3000I Product Flyer"
                  className="w-full h-auto transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </div>

            {/* Display Dashboard */}
            <div className="mt-8 flex justify-center">
              <div
                className="relative rounded-2xl overflow-hidden shadow-2xl cursor-zoom-in max-w-4xl w-full group"
                onClick={() => setLightboxImage({ src: '/images/Q7.png', title: 'Display Dashboard' })}
                data-testid="q7-image"
              >
                <img
                  src="/images/Q7.png"
                  alt="Display Dashboard"
                  className="w-full h-auto transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </div>

            {/* Application Examples */}
            <h3 className="text-3xl font-bold text-center text-gray-900 dark:text-white mt-20 mb-10" data-testid="application-examples-title">
              {t.products.examplesTitle}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {applicationExamples.map((example, index) => (
                <Card
                  key={index}
                  className="border-0 shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2 group overflow-hidden p-0"
                  data-testid={`example-card-${index}`}
                >
                  <div
                    className="relative h-56 overflow-hidden cursor-zoom-in"
                    onClick={() => setLightboxImage({ src: example.image, title: example.title })}
                  >
                    <img
                      src={example.image}
                      alt={example.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-purple-900/70 via-purple-900/10 to-transparent pointer-events-none" />
                    <div className="absolute bottom-0 left-0 right-0 p-4 pointer-events-none">
                      <h4 className="text-white text-lg font-semibold drop-shadow-lg">
                        {example.title}
                      </h4>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES SECTION */}
      <section id="services" className="py-20 bg-white dark:bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400 rounded-full text-sm font-semibold mb-4">
              {t.services.title}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4" data-testid="services-title">
              {t.services.subtitle}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto mb-16">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card
                  key={index}
                  className="border-0 shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2 group overflow-hidden p-0"
                  data-testid={`service-card-${index}`}
                >
                  {/* Service Image - clickable to enlarge */}
                  <div className="relative h-48 overflow-hidden cursor-zoom-in" onClick={() => setLightboxImage({ src: service.image, title: service.title })}>
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      data-testid={`service-image-${index}`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-purple-900/60 via-purple-900/10 to-transparent pointer-events-none" />
                    <div className="absolute top-4 left-4 w-12 h-12 rounded-xl bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm flex items-center justify-center shadow-lg pointer-events-none">
                      <Icon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                    </div>
                  </div>
                  <CardHeader>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base text-gray-600 dark:text-gray-400 leading-relaxed text-justify">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Industries Served */}
          <div className="bg-gradient-to-br from-purple-50 dark:from-slate-900 to-blue-50 dark:to-slate-800 rounded-3xl p-10">
            <h3 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-3">
              {t.industries.title}
            </h3>
            <p className="text-center text-gray-600 dark:text-gray-400 mb-10">
              {t.industries.subtitle}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
              {t.industries.list.map((industry, index) => (
                <div
                  key={index}
                  className="bg-white dark:bg-slate-900 p-4 rounded-xl shadow-sm text-center hover:shadow-md transition-shadow"
                  data-testid={`industry-${index}`}
                >
                  <p className="font-semibold text-gray-800 dark:text-gray-100 text-sm">{industry}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section id="faq" className="py-20 bg-white dark:bg-slate-900">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-400 rounded-full text-sm font-semibold mb-4">
              {t.faq.title}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              {t.faq.title}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {t.faq.subtitle}
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {t.faq.questions.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left text-lg font-semibold text-gray-800 dark:text-gray-100">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 dark:text-gray-400 text-base leading-relaxed whitespace-pre-line">
                  <span dangerouslySetInnerHTML={{ __html: item.a }} />
                  {item.image && (
                    <div className="mt-6 flex justify-center">
                      <div className="relative rounded-2xl overflow-hidden shadow-lg cursor-zoom-in group" onClick={() => setLightboxImage({ src: item.image, title: item.image.includes('Q8.png') ? '' : item.q })}>
                        <img src={item.image} alt="FAQ Illustration" className="w-full max-w-2xl h-auto transition-transform duration-500 group-hover:scale-105" />
                      </div>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CONTACT SECTION */}
      <section id="contact" className="py-20 bg-gray-50 dark:bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-400 rounded-full text-sm font-semibold mb-4">
              {t.contact.title}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4" data-testid="contact-title">
              {t.contact.subtitle}
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-6">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">{t.contact.info}</h3>
              {contactInfo.map((info, index) => {
                const Icon = info.icon;
                return (
                  <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-white mb-1">{info.title}</h4>
                          {info.link ? (
                            <a
                              onClick={() => {
                                if (info.link.startsWith('http')) {
                                  window.open(info.link, '_blank', 'noopener,noreferrer');
                                } else {
                                  window.location.href = info.link;
                                }
                              }}
                              role="button"
                              tabIndex={0}
                              className="text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:text-purple-400 transition-colors text-sm cursor-pointer"
                              data-testid={`contact-link-${index}`}
                            >
                              {info.content}
                            </a>
                          ) : (
                            <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">{info.content}</p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-xl">
                <CardHeader>
                  <CardTitle className="text-2xl">{t.contact.formTitle}</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <label htmlFor="name" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t.contact.name}
                        </label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Your Name"
                          data-testid="contact-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="email" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t.contact.email}
                        </label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="your_email@example.com"
                          data-testid="contact-email-input"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <label htmlFor="phone" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t.contact.phone}
                        </label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+62 xxx-xxxx-xxxx"
                          data-testid="contact-phone-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="subject" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t.contact.subject}
                        </label>
                        <Input
                          id="subject"
                          name="subject"
                          type="text"
                          required
                          value={formData.subject}
                          onChange={handleChange}
                          placeholder="Product Inquiry"
                          data-testid="contact-subject-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="message" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t.contact.message}
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        rows={5}
                        placeholder="Tell us about your project..."
                        data-testid="contact-message-input"
                      />
                    </div>
                    <div className="flex justify-center mb-4">
                      <div className="g-recaptcha" data-sitekey="6LdqQwUtAAAAAGy952KXXBquAVWrYIzaq5AtYc-H"></div>
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-purple-700 to-blue-600 hover:from-purple-800 hover:to-blue-700 text-white font-semibold transition-all hover:scale-105 shadow-lg"
                      data-testid="contact-submit-button"
                    >
                      {isSubmitting ? 'Sending...' : t.contact.send}
                      <Send className="ml-2 w-5 h-5" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* IMAGE LIGHTBOX */}
      <Dialog open={!!lightboxImage} onOpenChange={(open) => !open && setLightboxImage(null)}>
        <DialogContent
          className="max-w-5xl w-[95vw] p-0 border-0 bg-transparent shadow-none"
          data-testid="lightbox-dialog"
        >
          <DialogTitle className="sr-only">{lightboxImage?.title || 'Service Image'}</DialogTitle>
          {lightboxImage && (
            <div className="relative">
              <img
                src={lightboxImage.src}
                alt={lightboxImage.title}
                className="w-full h-auto max-h-[85vh] object-contain rounded-2xl shadow-2xl"
              />
              {lightboxImage.title && (
                <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-sm px-4 py-2 rounded-xl border border-white/20 shadow-lg max-w-[80%]">
                  <h3 className="text-white text-sm font-medium leading-relaxed">{lightboxImage.title}</h3>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Home;
